/**
 * IndexedDB-basierter Offline-Queue für SmartVolt Pro Zeiterfassung.
 *
 * Motivation:
 *  - Monteure sind häufig im Keller / Rohbau / Funkloch.
 *  - Zeit-Erfassung MUSS zuverlässig sein — auch wenn das Handy offline ist.
 *  - LocalStorage ist synchron und wird gerne von iOS Safari geleert.
 *  - IndexedDB ist persistent (auch nach Safari-Cache-Cleanup meistens erhalten)
 *    und asynchron.
 *
 * Design:
 *  - Datenbank "smartvolt-offline", ObjectStore "actions"
 *  - Jede Aktion hat: id, kind ("start"|"stop"), payload, client_timestamp, queued_at, tries, last_error
 *  - Beim Enqueue speichern wir IMMER die client_timestamp (echte Klick-Zeit).
 *  - flush() wird aufgerufen bei:
 *      • window.online-Event
 *      • Beim App-Start (falls Queue nicht leer)
 *      • Manuell (Button „Jetzt synchronisieren")
 *  - Ist die Queue älter als 1h → UI-Warnung.
 */

const DB_NAME = 'smartvolt-offline';
const DB_VERSION = 1;
const STORE = 'actions';

// -- LocalStorage-Fallback (falls IndexedDB nicht verfügbar, z.B. Privater Modus) -----------
const LS_KEY = 'smartvolt_offline_queue';
const useLocalStorage = () => {
  try {
    return typeof window !== 'undefined' && !window.indexedDB;
  } catch {
    return true;
  }
};

const lsReadAll = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
};
const lsWriteAll = (items) => localStorage.setItem(LS_KEY, JSON.stringify(items));

// -- IndexedDB Helpers -----------------------------------------------------------------------
const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        os.createIndex('queued_at', 'queued_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const idbAll = async () => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
};

const idbAdd = async (item) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add(item);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const idbUpdate = async (item) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(item);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const idbDelete = async (id) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

// -- Öffentliche API -------------------------------------------------------------------------
export const enqueueAction = async (kind, payload) => {
  const item = {
    kind, // "start" | "stop"
    payload,
    client_timestamp: new Date().toISOString(),
    queued_at: Date.now(),
    tries: 0,
    last_error: null };
  if (useLocalStorage()) {
    const all = lsReadAll();
    item.id = Date.now() + Math.random();
    all.push(item);
    lsWriteAll(all);
    return item.id;
  }
  return idbAdd(item);
};

export const listQueued = async () => {
  if (useLocalStorage()) return lsReadAll();
  return idbAll();
};

export const clearItem = async (id) => {
  if (useLocalStorage()) {
    lsWriteAll(lsReadAll().filter((i) => i.id !== id));
    return;
  }
  return idbDelete(id);
};

export const updateItem = async (item) => {
  if (useLocalStorage()) {
    const all = lsReadAll().map((i) => (i.id === item.id ? item : i));
    lsWriteAll(all);
    return;
  }
  return idbUpdate(item);
};

export const oldestQueueAgeMs = async () => {
  const items = await listQueued();
  if (items.length === 0) return 0;
  const oldest = Math.min(...items.map((i) => i.queued_at || Date.now()));
  return Date.now() - oldest;
};

/**
 * Flush alle offenen Aktionen gegen einen Endpoint-Mapper.
 * `endpointMap` = { start: (payload) => fetch-Promise, stop: (payload) => fetch-Promise }
 * Reihenfolge bleibt garantiert (start VOR stop einer Session).
 */
export const flushQueue = async (endpointMap) => {
  const items = (await listQueued()).sort((a, b) => (a.queued_at || 0) - (b.queued_at || 0));
  const results = { ok: 0, failed: 0, errors: [] };
  for (const item of items) {
    const handler = endpointMap[item.kind];
    if (!handler) {
      results.failed++;
      results.errors.push({ id: item.id, err: 'Unknown kind: ' + item.kind });
      continue;
    }
    try {
      const res = await handler(item.payload, item.client_timestamp);
      if (res && res.ok) {
        await clearItem(item.id);
        results.ok++;
      } else {
        item.tries = (item.tries || 0) + 1;
        item.last_error = res?.error || 'Server-Fehler';
        await updateItem(item);
        results.failed++;
        results.errors.push({ id: item.id, err: item.last_error });
      }
    } catch (err) {
      item.tries = (item.tries || 0) + 1;
      item.last_error = err?.message || String(err);
      await updateItem(item);
      results.failed++;
      results.errors.push({ id: item.id, err: item.last_error });
    }
  }
  return results;
};

/**
 * Registriert einen "online"-Listener der die Queue automatisch flusht.
 * Gibt eine Unsubscribe-Funktion zurück.
 */
export const registerAutoFlush = (flusher) => {
  const onOnline = () => {
    try { flusher(); } catch { /* ignore */ }
  };
  window.addEventListener('online', onOnline);
  return () => window.removeEventListener('online', onOnline);
};
